import React, { useEffect, useState } from 'react';
import { AlertTriangle, Key, X, ExternalLink } from 'lucide-react';
import { AUTH_ERROR_EVENT } from '../services/geminiService';

const APIErrorModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleEvent = () => setIsOpen(true);
    window.addEventListener(AUTH_ERROR_EVENT, handleEvent);
    return () => window.removeEventListener(AUTH_ERROR_EVENT, handleEvent);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-red-200">
        {/* Header */}
        <div className="bg-red-50 px-6 py-4 flex justify-between items-center border-b border-red-100">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-900">Intelligence Uplink Failed</h3>
              <p className="text-xs text-red-600 font-medium">Authentication Error Detected</p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="text-red-400 hover:text-red-700 transition-colors p-1 rounded-full hover:bg-red-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-slate-700 text-sm leading-relaxed">
            The War Room Dashboard cannot connect to the <span className="font-semibold text-slate-900">Gemini AI Service</span>. 
            This usually happens when the API Key is missing or incorrect in your environment configuration.
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-2">
              <Key className="w-4 h-4 text-bjp-saffron" /> Troubleshooting Steps
            </h4>
            <ul className="list-disc list-inside text-xs text-slate-600 space-y-2">
              <li>
                <span className="font-medium">Cloudflare Pages:</span> Go to <span className="font-mono bg-slate-200 px-1 rounded">Settings &gt; Environment Variables</span> and ensure <span className="font-mono text-slate-800">VITE_API_KEY</span> is set correctly.
              </li>
              <li>
                <span className="font-medium">Local Development:</span> Check your <span className="font-mono bg-slate-200 px-1 rounded">.env</span> file contains <span className="font-mono text-slate-800">VITE_API_KEY=your_key_here</span>.
              </li>
              <li>
                If you just updated the key, please redeploy or restart the server.
              </li>
            </ul>
          </div>
          
          <div className="flex gap-3 mt-4">
             <a 
               href="https://aistudio.google.com/app/apikey" 
               target="_blank" 
               rel="noreferrer"
               className="flex-1 flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-700 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
             >
               Get API Key <ExternalLink className="w-3 h-3" />
             </a>
             <button 
               onClick={() => window.location.reload()}
               className="flex-1 bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm"
             >
               Reload Application
             </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-center">
           <p className="text-[10px] text-slate-400">Error Code: ERR_GEMINI_AUTH_403</p>
        </div>
      </div>
    </div>
  );
};

export default APIErrorModal;